import React from 'react';
import { Paragraph, Typography } from '../../designComponents';
import Timeline from '../../designComponents/Timeline';
import { InfoRowStyled, VerticalSpaceStyled } from '../../style/reusableStyle';
import { ContractDetailsContainerStyled } from './style';

type ProposalStatusChange = {
  status: string;
  changeAt: string;
  _id: string;
};

type ContractDetailProps = {
  contract: {
    startingDate?: string;
    endDate?: string;
    specification?: string;
    experience?: string;
    numberOfExperience?: number;
    cost?: number;
    workApproach?: string;
    riskAssessment?: string;
    designApproach?: string;
    pastClients?: string[];
    pastProject?: string[];
    qualifications?: string;
    proposalStatusChangedDate?: ProposalStatusChange[];
  };
};

const ContractDetail: React.FC<ContractDetailProps> = ({ contract }) => {
  if (!contract) return null;
  // Build timeline dynamically
  const timeline = [];
  if (contract.startingDate)
    timeline.push({ date: contract.startingDate, title: 'Starting Date' });
  if (contract.endDate)
    timeline.push({ date: contract.endDate, title: 'End Date' });

  return (
    <ContractDetailsContainerStyled>
      <Typography
        variant="normal"
        className="title-header"
        fontColor="secondary"
        strong
      >
        CONTRACT DETAILS
      </Typography>

      <VerticalSpaceStyled>
        {/* Timeline */}
        {timeline.length > 0 && (
          <InfoRowStyled>
            <Typography className="title">Timeline :</Typography>
            <Timeline events={timeline} />
          </InfoRowStyled>
        )}

        {/* Specification */}
        {contract.specification && (
          <InfoRowStyled $direction="column">
            <Typography className="title">Specification :</Typography>
            <Paragraph
              className="value"
              fontColor="primary"
              text={contract.specification}
            />
          </InfoRowStyled>
        )}

        {/* Experience */}
        {contract.experience && (
          <InfoRowStyled>
            <Typography className="title">Experience :</Typography>
            <Typography className="value">
              {contract.experience} ({contract.numberOfExperience} years)
            </Typography>
          </InfoRowStyled>
        )}

        {/* Cost Estimation */}
        {contract.cost !== undefined && (
          <InfoRowStyled>
            <Typography className="title">Cost Estimation :</Typography>
            <Typography className="value">€ {contract.cost}</Typography>
          </InfoRowStyled>
        )}

        {/* Work Approach */}
        {contract.workApproach && (
          <InfoRowStyled>
            <Typography className="title">Work Approach :</Typography>
            <Typography className="value">{contract.workApproach}</Typography>
          </InfoRowStyled>
        )}

        {/* Risk Assessment */}
        {contract.riskAssessment && (
          <InfoRowStyled>
            <Typography className="title">Risk Assessment :</Typography>
            <Typography className="value">{contract.riskAssessment}</Typography>
          </InfoRowStyled>
        )}

        {/* Design Approach */}
        {contract.designApproach && (
          <InfoRowStyled $direction="column">
            <Typography className="title">Design Approach :</Typography>
            <Paragraph
              className="value"
              fontColor="primary"
              text={contract.designApproach}
            />
          </InfoRowStyled>
        )}

        {/* Past Clients */}
        {contract.pastClients && contract.pastClients.length > 0 && (
          <InfoRowStyled $direction="column">
            <Typography className="title">Past Clients :</Typography>
            {contract.pastClients.map((client, idx) => (
              <Typography className="value" key={idx}>
                {client}
              </Typography>
            ))}
          </InfoRowStyled>
        )}

        {/* Past Projects */}
        {contract.pastProject && contract.pastProject.length > 0 && (
          <InfoRowStyled $direction="column">
            <Typography className="title">Past Projects :</Typography>
            {contract.pastProject.map((imgUrl, idx) => (
              <img
                key={idx}
                src={imgUrl}
                alt={`Past Project ${idx + 1}`}
                style={{ maxWidth: '200px', marginBottom: '8px' }}
              />
            ))}
          </InfoRowStyled>
        )}

        {/* Qualifications */}
        {contract.qualifications && (
          <InfoRowStyled>
            <Typography className="title">Qualifications :</Typography>
            <Typography className="value">{contract.qualifications}</Typography>
          </InfoRowStyled>
        )}

        {/* Proposal Status History */}
        {contract.proposalStatusChangedDate &&
          contract.proposalStatusChangedDate.length > 0 && (
            <InfoRowStyled $direction="column">
              <Typography className="title">
                Proposal Status History :
              </Typography>
              {contract.proposalStatusChangedDate.map((statusObj) => (
                <Typography className="value" key={statusObj._id}>
                  {statusObj.status} —{' '}
                  {new Date(statusObj.changeAt).toLocaleDateString()}
                </Typography>
              ))}
            </InfoRowStyled>
          )}
      </VerticalSpaceStyled>
    </ContractDetailsContainerStyled>
  );
};

export default ContractDetail;
